import { A } from "@solidjs/router";
import {
  IoChevronForward,
  IoStarOutline,
  IoTrashOutline,
} from "solid-icons/io";
import {
  type Component,
  createMemo,
  createSignal,
  For,
  Show,
  Suspense,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loading } from "@/components/ui/loading";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import {
  type IdentifiedProfileRecommendation,
  type PendingRecommendationGiven,
  type ProfileRecommendation,
  useCancelInvitation,
  useDeleteRecommendation,
  useGetProfileRecommendations,
} from "@/domains/company/invitations/api";
import { publicProfilePath } from "@/domains/freelance/profile/utils/public-profile-link";

interface RecommendationsSectionProps {
  profileId: string;
  /** Mostra il pulsante "Raccomanda un collega" nello stato vuoto. Default: true (es. nella spalla profilo) */
  showRecommendButton?: boolean;
  /** Nasconde il titolo e sottotitolo (utile quando è già presente nel wrapper) */
  hideTitle?: boolean;
  /**
   * Nasconde l'intera sezione quando non ci sono raccomandazioni, invece di
   * mostrare lo stato vuoto con la CTA. Usato nelle spalle in sola lettura
   * (sheet ricerca attiva / profilo pubblico) dove lo stato vuoto non è utile.
   */
  hideWhenEmpty?: boolean;
  /** Quando fornito, sostituisce la navigazione predefinita dal CTA dello stato vuoto. */
  onRecommend?: () => void;
  /**
   * `"public"` forza la vista pubblica **anche quando chi guarda è il
   * proprietario**.
   *
   * Serve all'anteprima "Profilo Pubblico" e a `/profile/<proprio id>`: senza,
   * il proprietario vedrebbe la propria vista privilegiata (pendenti, revoca,
   * chip cliccabili con foto) e crederebbe che sia ciò che vedono gli altri —
   * un'anteprima che mente su ciò che sta per condividere (AC10, AC10b).
   *
   * Omesso = comportamento di sempre, così la tab delle raccomandazioni del
   * proprietario non cambia.
   */
  viewAs?: "owner" | "public";
}

/**
 * Il raccomandante come lo vede un visitatore **anonimo**, o il proprietario
 * nell'anteprima: nome e ruolo, nient'altro.
 *
 * Niente link e niente foto per scelta di prodotto (D6): la persona che ha
 * lasciato la raccomandazione non ha acconsentito a comparire, con volto e
 * profilo cliccabile, su una pagina aperta al mondo. La vista anonima del
 * backend non manda affatto `profile_id` e `avatar_url`: qui non c'è un campo
 * nascosto, non c'è proprio il dato. È anche il chip di riserva per una riga
 * **senza** `profile_id` sotto qualunque sessione: un link a
 * `/profile/undefined` è peggio dell'assenza di link.
 *
 * Una `Card` non cliccabile, non un `<A>` disabilitato: un link che non porta
 * da nessuna parte è peggio dell'assenza di link.
 */
const PublicRecommendationChip: Component<{
  recommendation: ProfileRecommendation;
  profileFallback: string;
}> = (props) => (
  <Card class="w-full shadow-none border bg-white px-4 py-3">
    <div class="flex min-w-0 flex-1 flex-col gap-0.5">
      <span class="font-medium text-gray-900 truncate">
        {props.recommendation.full_name || props.profileFallback}
      </span>
      <Show when={props.recommendation.job_title}>
        <span class="text-sm text-gray-500 truncate">
          {props.recommendation.job_title}
        </span>
      </Show>
    </div>
  </Card>
);

const RecommendationChip: Component<{
  /**
   * `IdentifiedProfileRecommendation` e non `ProfileRecommendation`: questo chip
   * costruisce due `href={/profile/${profile_id}}`, quindi pretende l'id invece
   * di interpolarne uno `undefined`. Il chiamante deve restringere.
   *
   * I link aprono una **nuova scheda**: chi sta valutando un candidato in una
   * sheet non deve perdere il flusso per guardare chi lo raccomanda.
   */
  recommendation: IdentifiedProfileRecommendation;
  profileFallback: string;
  /** Quando fornito, mostra il pulsante revoca (solo per raccomandazioni date). */
  onDelete?: () => void;
  isDeleting?: boolean;
}> = (props) => {
  const { t } = useI18n();
  const [revokeModalOpen, setRevokeModalOpen] = createSignal(false);
  const statusLabel = () =>
    props.recommendation.status === "accepted"
      ? t("recommendations.statusAccepted")
      : null;
  const onboardingStatus = () => props.recommendation.onboarding_status;

  const handleRevokeClick = () => {
    if (props.isDeleting) return;
    setRevokeModalOpen(true);
  };

  const handleConfirmRevoke = () => {
    props.onDelete?.();
    setRevokeModalOpen(false);
  };

  return (
    <>
      <Card class="group w-full shadow-none border flex items-center gap-3 bg-white px-4 py-3 transition-[box-shadow,border-color,translate] duration-200 hover:border-matisse-200 hover:shadow-md hover:-translate-y-0.5">
        <A
          href={publicProfilePath({
            id: props.recommendation.profile_id,
            username: props.recommendation.username ?? null,
          })}
          class="flex min-w-0 flex-1 items-center gap-3"
          onClick={(e) => e.stopPropagation()}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Show
            when={props.recommendation.avatar_url}
            fallback={
              <div class="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-sm font-semibold bg-matisse-100 text-matisse-700">
                {
                  (props.recommendation.full_name ??
                    props.recommendation.username ??
                    "?")[0]
                }
              </div>
            }
          >
            <img
              src={props.recommendation.avatar_url ?? ""}
              alt=""
              class="h-10 w-10 shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm"
            />
          </Show>
          <div class="flex-1 min-w-0 flex flex-col gap-0.5">
            <span class="font-medium text-gray-900 truncate">
              {props.recommendation.full_name ||
                props.recommendation.username ||
                props.profileFallback}
            </span>
            <Show when={props.recommendation.job_title}>
              <span class="text-sm text-gray-500 truncate">
                {props.recommendation.job_title}
              </span>
            </Show>
            {/*
              Forma a callback: `onboarding_status` è ora opzionale, quindi il
              literal della chiave degrada a `string` e `t()` tornerebbe
              `unknown`. L'accessor della callback è non-nullable, così la
              chiave resta un'unione di letterali.
            */}
            <Show when={onboardingStatus()}>
              {(status) => (
                <span class="text-xs text-gray-400 truncate">
                  {t(`recommendations.onboarding_status.${status()}`)}
                </span>
              )}
            </Show>
          </div>
        </A>
        <div class="flex shrink-0 items-center gap-1.5">
          <Show when={statusLabel()}>
            <Badge variant="secondary" size="sm">
              {statusLabel()}
            </Badge>
          </Show>
          <Show when={props.onDelete}>
            <button
              type="button"
              aria-label={t("recommendations.deleteRecommendation")}
              disabled={props.isDeleting}
              class="rounded p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRevokeClick();
              }}
            >
              <IoTrashOutline size={16} />
            </button>
          </Show>
          {/*
            Duplicato **decorativo** del link principale: stessa destinazione,
            solo il chevron. `aria-hidden` lo toglie agli screen reader, ma da
            solo non basta — un elemento nascosto all'assistive tech e ancora
            raggiungibile col tab è una tappa senza nome accessibile. Serve
            `tabindex="-1"`, che lo esclude dall'ordine di tabulazione lasciando
            il click al mouse.
          */}
          <A
            href={publicProfilePath({
              id: props.recommendation.profile_id,
              username: props.recommendation.username ?? null,
            })}
            class="shrink-0 rounded p-1 text-gray-300 transition-colors hover:text-matisse-500"
            onClick={(e) => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
            aria-hidden
            tabindex={-1}
          >
            <IoChevronForward size={16} />
          </A>
        </div>
      </Card>
      <Show when={props.onDelete}>
        <Dialog open={revokeModalOpen()} onOpenChange={setRevokeModalOpen}>
          <DialogContent class="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                {t("recommendations.revokeConfirmTitle")}
              </DialogTitle>
              <DialogDescription>
                {t("recommendations.revokeConfirmDescription")}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter class="gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setRevokeModalOpen(false)}
                disabled={props.isDeleting}
              >
                {t("recommendations.revokeConfirmCancel")}
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmRevoke}
                disabled={props.isDeleting}
              >
                {t("recommendations.revokeConfirmButton")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Show>
    </>
  );
};

/** Chip per un invito in attesa (email invitata, non ancora accettata). */
const PendingInvitationChip: Component<{
  invitation: PendingRecommendationGiven;
  onCancel: (id: string) => void;
  isCancelling: boolean;
}> = (props) => {
  const { t } = useI18n();
  const [cancelModalOpen, setCancelModalOpen] = createSignal(false);
  const daysUntilExpiry = createMemo(() => {
    const expires = new Date(props.invitation.expires_at).getTime();
    const now = Date.now();
    const days = Math.max(
      0,
      Math.ceil((expires - now) / (24 * 60 * 60 * 1000)),
    );
    return days;
  });
  const truncatedEmail =
    props.invitation.email.length > 28
      ? `${props.invitation.email.slice(0, 25)}...`
      : props.invitation.email;

  const handleConfirmCancel = () => {
    props.onCancel(props.invitation.id);
    setCancelModalOpen(false);
  };

  return (
    <>
      <Card class="relative w-full shadow-none border flex items-center gap-3 bg-white px-4 py-3">
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500">
          <i class="ph ph-envelope-simple text-lg" />
        </div>
        <div class="flex-1 min-w-0 flex flex-col gap-0.5">
          <span
            class="font-medium text-gray-900 truncate"
            title={props.invitation.email}
          >
            {truncatedEmail}
          </span>
          <span class="text-xs text-gray-500">
            {t("recommendations.pendingExpires", { days: daysUntilExpiry() })}
          </span>
        </div>
        <Badge variant="secondary" size="sm">
          {t("recommendations.statusPending")}
        </Badge>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          class="text-gray-500 hover:text-red-600"
          disabled={props.isCancelling}
          onClick={() => setCancelModalOpen(true)}
        >
          {t("recommendations.cancelInvitation")}
        </Button>
      </Card>
      <Dialog open={cancelModalOpen()} onOpenChange={setCancelModalOpen}>
        <DialogContent class="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {t("recommendations.cancelInvitationConfirmTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("recommendations.cancelInvitationConfirmDescription")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter class="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setCancelModalOpen(false)}
              disabled={props.isCancelling}
            >
              {t("recommendations.cancelInvitationConfirmCancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={props.isCancelling}
            >
              {t("recommendations.cancelInvitationConfirmButton")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const RecommendationsSection: Component<RecommendationsSectionProps> = (
  props,
) => {
  const { t } = useI18n();
  const [authStoreV2] = useAuthV2();
  // Accessor: `props.profileId` cambia quando la sheet passa a un altro
  // freelancer senza rimontare, e la query deve seguirlo.
  const recommendationsQuery = useGetProfileRecommendations(
    () => props.profileId,
  );
  const deleteRecommendationMutation = useDeleteRecommendation();
  const cancelInvitationMutation = useCancelInvitation();

  /**
   * Chi guarda **è** la persona del profilo, indipendentemente da come la
   * sezione è montata. Separato da `isOwnProfile` di proposito: quello dice
   * «rendi la vista privilegiata», questo dice «sei tu», e `viewAs="public"`
   * spegne il primo senza cambiare il secondo.
   */
  const isViewerTheProfileOwner = createMemo(
    () =>
      !!authStoreV2?.profile?.id && authStoreV2.profile.id === props.profileId,
  );

  const isOwnProfile = createMemo(
    () => props.viewAs !== "public" && isViewerTheProfileOwner(),
  );

  /**
   * Un visitatore **loggato** che non è il proprietario — chi valuta un
   * candidato dalle sheet aziendali — vede i raccomandanti con foto e link al
   * profilo, in una nuova scheda: è l'identità di chi firma a dare credibilità
   * alla raccomandazione (AC5b, emendata il 2026-09-10). L'anonimo non ha
   * sessione e vede nome e ruolo; il proprietario in anteprima vede la stessa
   * forma ridotta, perché l'anteprima deve mostrargli la vista di un estraneo
   * (AC10, AC10b).
   *
   * Deliberatamente **non** guarda `viewAs`: l'unico punto che monta questa
   * sezione fuori dalla tab del proprietario è `ProfileInfo`, che passa
   * `viewAs="public"` sempre — pagina pubblica e sheet aziendali comprese.
   * Legandolo a `viewAs` il predicato sarebbe sempre falso in produzione e
   * l'identità non comparirebbe a nessuno.
   */
  const showsRecommenderIdentity = createMemo(
    () => !!authStoreV2?.profile?.id && !isViewerTheProfileOwner(),
  );

  /**
   * **L'unica** lettura di `recommendationsQuery.data` del componente, e solo a
   * query risolta.
   *
   * `createMemo` in Solid è *eager*: calcola il proprio valore nell'istante in
   * cui viene creato, cioè nel corpo di questo componente — **fuori** dal
   * `<Suspense>` che il `return` più sotto costruisce. Ogni memo che leggesse
   * `data` direttamente sospenderebbe quindi il confine del **chiamante**.
   * Effetto misurato prima di questa modifica: con le raccomandazioni ancora in
   * volo spariva l'intera `ProfileInfo` — nome, ruolo, skill — sostituita dallo
   * scheletro, mentre esperienze e formazione (che leggono `data` in JSX dietro
   * `isSuccess`, quindi pigramente) non lo facevano.
   *
   * Passando dal gate `isSuccess`, il primo calcolo non tocca `data`: non c'è
   * risorsa da sospendere. Al termine della fetch `isSuccess` cambia, il memo
   * ricalcola e `data` è già lì.
   */
  const settledData = createMemo(() =>
    recommendationsQuery.isSuccess ? recommendationsQuery.data : undefined,
  );

  const receivedCount = createMemo(
    () => settledData()?.recommendationsReceived?.length ?? 0,
  );

  const pendingGiven = createMemo(() => settledData()?.pendingGiven ?? []);

  /** Profili che sono in "recommendationsGiven" (per mostrare revoca solo su quelli). */
  const givenProfileIds = createMemo(() => {
    const data = settledData();
    if (!data) return new Set<string>();
    const set = new Set<string>();
    for (const r of data.recommendationsGiven ?? []) {
      if (r.profile_id) set.add(r.profile_id);
    }
    return set;
  });

  /**
   * Lista unificata: prima ricevute, poi date; traccia quali sono "given" per la
   * revoca.
   *
   * Scarta le righe senza `profile_id`. Sono impossibili sulla vista
   * proprietario, che è l'unica a raggiungere questo memo, ma la deduplica gira
   * su quell'id e senza il filtro un `undefined` diventerebbe una chiave valida
   * del `Set`: due raccomandanti anonimi collasserebbero in uno.
   */
  const mergedRecommendations = createMemo(
    (): IdentifiedProfileRecommendation[] => {
      const data = settledData();
      if (!data || !isOwnProfile()) return [];
      const seen = new Set<string>();
      const result: IdentifiedProfileRecommendation[] = [];
      for (const r of [
        ...(data.recommendationsReceived ?? []),
        ...(data.recommendationsGiven ?? []),
      ]) {
        if (!r.profile_id || seen.has(r.profile_id)) continue;
        seen.add(r.profile_id);
        result.push({ ...r, profile_id: r.profile_id });
      }
      return result;
    },
  );

  const recommendationsReceivedList = createMemo(
    () => settledData()?.recommendationsReceived ?? [],
  );

  const activeCount = createMemo(
    () =>
      (settledData()?.recommendationsGiven?.length ?? 0) +
      (settledData()?.recommendationsReceived?.length ?? 0),
  );

  const hasRecommendations = createMemo(() => {
    if (!recommendationsQuery.isSuccess) return false;
    if (isOwnProfile())
      return mergedRecommendations().length > 0 || pendingGiven().length > 0;
    return receivedCount() > 0;
  });

  /** In read-only side panels, collapse the section entirely once we know it's empty. */
  const collapseEmpty = createMemo(
    () =>
      !!props.hideWhenEmpty &&
      recommendationsQuery.isSuccess &&
      !hasRecommendations(),
  );

  const handleCancelInvitation = (invitationId: string) => {
    cancelInvitationMutation.mutate(invitationId);
  };

  return (
    <Suspense fallback={<Loading />}>
      {/*
        Il caricamento si gestisce qui, **non** lasciandolo a `Suspense`.

        I memo che leggono `recommendationsQuery.data` (`activeCount`,
        `recommendationsReceivedList`, `pendingGiven`) sono creati nel corpo del
        componente, cioè **fuori** da questo confine: solid-query sospende alla
        prima lettura di `data`, e la sospensione si registra sul contesto
        catturato quando il memo è stato creato — il `Suspense` del chiamante,
        non questo. Effetto misurato: con le raccomandazioni ancora in volo
        spariva l'intera `ProfileInfo`, nome e ruolo compresi, sostituita dallo
        scheletro; le sezioni esperienze e formazione non lo facevano perché
        leggono `data` solo dietro `isSuccess`.

        Gating esplicito su `isPending`: finché la query non ha risposto nessun
        memo tocca `data`, quindi non c'è nulla da sospendere e il resto della
        pagina resta leggibile. Il `Suspense` resta come rete.
      */}
      <Show when={recommendationsQuery.isPending}>
        <Loading />
      </Show>
      {/*
        Un guasto **non** è un'assenza.

        Con `hideWhenEmpty` la sezione collassa quando non ci sono
        raccomandazioni: se collassasse anche in errore, una rete caduta
        racconterebbe "questa persona non è raccomandata da nessuno" — una
        falsità sul conto di qualcun altro, e senza alcun modo di accorgersene.
        Blocco compatto con "Riprova" al suo posto (AC4c).
      */}
      <Show when={recommendationsQuery.isError}>
        <div class="flex flex-col items-start gap-2 py-2">
          <p class="text-sm text-muted-foreground">
            {t("recommendations.section_error")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void recommendationsQuery.refetch()}
          >
            {t("common.retry")}
          </Button>
        </div>
      </Show>
      <Show
        when={
          !recommendationsQuery.isPending &&
          !recommendationsQuery.isError &&
          !collapseEmpty()
        }
      >
        <div class="space-y-6">
          <Show when={!props.hideTitle}>
            <div class="flex items-center gap-3">
              <div>
                <h2 class="text-lg font-semibold tracking-tight text-gray-900">
                  {t("recommendations.title")}
                </h2>
                {/*
                  Il sottotitolo parla a chi guarda, non del profilo: in
                  seconda persona ha senso solo per il proprietario. Un
                  visitatore vede le sole ricevute, quindi la frase che
                  promette entrambe le direzioni sarebbe falsa oltre che
                  rivolta alla persona sbagliata. Stessa coppia che lo stato
                  vuoto usa poche righe più sotto.
                */}
                <p class="text-xs text-gray-500">
                  {isOwnProfile()
                    ? t("recommendations.subtitleOwn")
                    : t("recommendations.subtitleOther")}
                </p>
              </div>
            </div>
          </Show>

          <Show
            when={hasRecommendations()}
            fallback={
              <Card class="overflow-hidden border-0 bg-linear-to-br from-matisse-50/80 via-white to-gray-50/50 shadow-sm w-full">
                <CardContent class="flex flex-col items-center justify-center py-12 px-6">
                  <p class="text-center text-base font-medium text-gray-800">
                    {isOwnProfile()
                      ? t("recommendations.emptyOwn")
                      : t("recommendations.emptyOther")}
                  </p>
                  <p class="mt-2 max-w-sm text-center text-sm leading-relaxed text-gray-500">
                    {isOwnProfile()
                      ? t("recommendations.emptyDescriptionOwn")
                      : t("recommendations.emptyDescriptionOther")}
                  </p>
                  <Show
                    when={isOwnProfile() && props.showRecommendButton !== false}
                  >
                    <Show
                      when={props.onRecommend}
                      fallback={
                        <Button
                          as={A}
                          href="/profile?tab=recommendations"
                          variant="default"
                          size="sm"
                          class="mt-6 shadow-sm hover:shadow-md"
                        >
                          <IoStarOutline class="mr-2 h-4 w-4" />
                          {t("recommendations.recommendButton")}
                        </Button>
                      }
                    >
                      {(onRecommend) => (
                        <Button
                          variant="default"
                          size="sm"
                          class="mt-6 shadow-sm hover:shadow-md"
                          onClick={() => onRecommend()()}
                        >
                          <IoStarOutline class="mr-2 h-4 w-4" />
                          {t("recommendations.recommendButton")}
                        </Button>
                      )}
                    </Show>
                  </Show>
                </CardContent>
              </Card>
            }
          >
            <div class="space-y-6">
              <Show
                when={isOwnProfile()}
                fallback={
                  <div class="space-y-3">
                    <h3 class="text-sm font-medium text-gray-700">
                      {t("recommendations.recommendedBy")}
                    </h3>
                    <div class="grid gap-2 w-full">
                      {/*
                        Solo le **ricevute**, mai le date né i pendenti: sono
                        viste del proprietario (AC8b), anche se un payload le
                        portasse. Il chip con identità pretende `profile_id`:
                        una riga senza (BE anonimo, o più vecchio) ricade sul
                        chip a nome e ruolo invece di linkare
                        `/profile/undefined`.
                      */}
                      <For each={recommendationsReceivedList()}>
                        {(recommendation) => (
                          <Show
                            when={
                              showsRecommenderIdentity() &&
                              recommendation.profile_id
                            }
                            fallback={
                              <PublicRecommendationChip
                                recommendation={recommendation}
                                profileFallback={t(
                                  "recommendations.profileFallback",
                                )}
                              />
                            }
                          >
                            {(profileId) => (
                              <RecommendationChip
                                recommendation={{
                                  ...recommendation,
                                  profile_id: profileId(),
                                }}
                                profileFallback={t(
                                  "recommendations.profileFallback",
                                )}
                              />
                            )}
                          </Show>
                        )}
                      </For>
                    </div>
                  </div>
                }
              >
                <Show when={activeCount() > 0} fallback={null}>
                  <div class="space-y-3">
                    <h3 class="text-sm font-medium text-gray-700">
                      {t("recommendations.activeSection")} (
                      {mergedRecommendations().length})
                    </h3>
                    <div class="grid gap-2 w-full">
                      <For each={mergedRecommendations()}>
                        {(recommendation) => (
                          <RecommendationChip
                            recommendation={recommendation}
                            profileFallback={t(
                              "recommendations.profileFallback",
                            )}
                            onDelete={
                              givenProfileIds().has(recommendation.profile_id)
                                ? () =>
                                    deleteRecommendationMutation.mutate(
                                      recommendation.id,
                                    )
                                : undefined
                            }
                            isDeleting={
                              deleteRecommendationMutation.isPending &&
                              deleteRecommendationMutation.variables ===
                                recommendation.id
                            }
                          />
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
                <Show when={pendingGiven().length > 0}>
                  <div class="space-y-3">
                    <h3 class="text-sm font-medium text-gray-700">
                      {t("recommendations.pendingSection")} (
                      {pendingGiven().length})
                    </h3>
                    <div class="grid gap-2 w-full">
                      <For each={pendingGiven()}>
                        {(invitation) => (
                          <PendingInvitationChip
                            invitation={invitation}
                            onCancel={handleCancelInvitation}
                            isCancelling={cancelInvitationMutation.isPending}
                          />
                        )}
                      </For>
                    </div>
                  </div>
                </Show>
              </Show>
            </div>
          </Show>
        </div>
      </Show>
    </Suspense>
  );
};
